import React from 'react';
import { Paper, Box, Typography, Divider, Button } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTranslation } from 'react-i18next';
import { IC, IFontSizes, IFontWeights, IRadius, INumericSx, ILineHeight } from '../insightsTheme';

/**
 * ActionsInProgress — ongoing/pending CM experiments as dense table.
 *
 * Props:
 *   actions: [{ id, n, nCounterpart, nSep, hp ('grn'|'amb'|'red'), dB, elapsed, dur }]
 *     n / nCounterpart / nSep: outgoing product, incoming product and the separator between them
 *     ('→' replacement, '+' combo). nCounterpart is null for single-product actions (price moves).
 *   total, onTrack: counts
 *   loading: shimmer first-frame
 *   onSeeMore: navigation callback
 */
const ActionsInProgress = ({ actions = [], total, onTrack, loading = false, onSeeMore, onRowClick }) => {
  const { t } = useTranslation();
  const shown = actions.slice(0, 6);
  // Only sum rows where dB is a real number; nulls are intentionally excluded.
  const sumDelta = actions.reduce((s, a) => (a.dB == null ? s : s + Number(a.dB)), 0);
  const hasAnyDelta = actions.some((a) => a.dB != null);
  const shimmer = {
    background: 'linear-gradient(90deg, rgba(0,0,0,0.05) 25%, rgba(0,0,0,0.02) 50%, rgba(0,0,0,0.05) 75%)',
    backgroundSize: '200% 100%',
    animation: 'ins-v2-shimmer 1.3s linear infinite',
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        flex: 1,
        minWidth: 0,
        borderRadius: `${IRadius.card}px`,
        borderColor: IC.bdr,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${IC.bdr}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Typography sx={{ fontSize: `${IFontSizes.md}px`, fontWeight: IFontWeights.strong, color: IC.tx }}>
          {t('insights_v2_actions_title')}
        </Typography>
        <Typography sx={{ fontSize: `${IFontSizes.xs}px`, color: IC.txM }}>
          {t('insights_v2_actions_count', { count: total != null ? total : actions.length })}
          {onTrack != null && ` · ${t('insights_v2_actions_on_track', { count: onTrack })}`}
        </Typography>
      </Box>

      <Box sx={{ flex: 1 }}>
        {shown.length === 0 && loading
          ? [0, 1, 2, 3, 4].map((i) => (
              <Box
                key={`sk-${i}`}
                sx={{ display: 'flex', gap: 1.5, p: 1.25, borderBottom: `1px solid ${IC.bdrL}`, alignItems: 'center' }}
              >
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', ...shimmer }} />
                <Box sx={{ width: 42, height: 10, borderRadius: 0.5, ...shimmer }} />
                <Box sx={{ flex: 1, height: 10, borderRadius: 0.5, ...shimmer }} />
                <Box sx={{ width: 60, height: 10, borderRadius: 0.5, ...shimmer }} />
                <Box sx={{ width: 60, height: 10, borderRadius: 0.5, ...shimmer }} />
              </Box>
            ))
          : null}
        {shown.length === 0 && !loading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography sx={{ fontSize: `${IFontSizes.sm}px`, color: IC.txM }}>
              {t('insights_v2_actions_empty')}
            </Typography>
          </Box>
        ) : null}
        {shown.map((a, i) => {
          const pct = a.dur > 0 ? Math.round((a.elapsed / a.dur) * 100) : 0;
          const hpC = a.hp === 'grn' ? IC.grn : a.hp === 'amb' ? IC.amb : IC.red;
          const isUp = a.dB > 0;
          const clickable = !!onRowClick;
          return (
            <Box
              key={i}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : -1}
              onClick={clickable ? () => onRowClick(a) : undefined}
              onKeyDown={(e) => {
                if (clickable && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  onRowClick(a);
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 2,
                py: 1.1,
                borderBottom: i < shown.length - 1 ? `1px solid ${IC.bdrL}` : 'none',
                cursor: clickable ? 'pointer' : 'default',
                transition: 'background 0.15s ease',
                '&:hover': clickable ? { background: IC.surfaceAlt } : undefined,
              }}
            >
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: hpC, flexShrink: 0 }} />
              <Typography
                sx={{
                  fontSize: `${IFontSizes.xs}px`,
                  color: IC.txM,
                  fontWeight: IFontWeights.label,
                  width: 48,
                  flexShrink: 0,
                  ...INumericSx,
                }}
              >
                {a.id}
              </Typography>
              <Box
                sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}
                title={a.nCounterpart ? `${a.n} ${a.nSep} ${a.nCounterpart}` : a.n}
              >
                <Typography
                  sx={{
                    minWidth: 0,
                    fontSize: `${IFontSizes.sm}px`,
                    fontWeight: IFontWeights.label,
                    color: IC.tx,
                    lineHeight: ILineHeight.feed,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {a.n}
                </Typography>
                {/* The incoming product is what earned the euros on this row — it never gets
                    truncated away, only the outgoing one does. */}
                {a.nCounterpart && (
                  <>
                    <Typography
                      sx={{ fontSize: `${IFontSizes.xs}px`, color: IC.txM, flexShrink: 0 }}
                    >
                      {a.nSep}
                    </Typography>
                    <Typography
                      sx={{
                        flexShrink: 0,
                        maxWidth: 120,
                        fontSize: `${IFontSizes.sm}px`,
                        fontWeight: IFontWeights.label,
                        color: IC.tx,
                        lineHeight: ILineHeight.feed,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {a.nCounterpart}
                    </Typography>
                  </>
                )}
              </Box>
              <Typography
                sx={{
                  width: 72,
                  textAlign: 'right',
                  flexShrink: 0,
                  fontSize: `${IFontSizes.md}px`,
                  fontWeight: IFontWeights.strong,
                  color: a.dB == null ? IC.txM : isUp ? IC.grn : a.dB === 0 ? IC.txM : IC.red,
                  ...INumericSx,
                }}
              >
                {a.dB == null ? '—' : `${isUp ? '+' : ''}${Math.round(Number(a.dB))}€`}
              </Typography>
              <Box sx={{ width: 80, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ flex: 1, height: 4, borderRadius: 2, background: IC.bdrL }}>
                  <Box sx={{ width: `${pct}%`, height: '100%', borderRadius: 2, background: hpC }} />
                </Box>
                <Typography sx={{ fontSize: `${IFontSizes.xs}px`, color: IC.txM, ...INumericSx }}>
                  {pct}%
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      <Divider sx={{ borderColor: IC.bdr }} />
      <Box sx={{ px: 2, py: 1, background: IC.surfaceAlt, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ fontSize: `${IFontSizes.xs}px`, color: IC.txM }}>
          {t('insights_v2_actions_total_benefit')}:{' '}
          <Box
            component="strong"
            sx={{
              color: !hasAnyDelta ? IC.txM : sumDelta >= 0 ? IC.grn : IC.red,
              fontWeight: IFontWeights.strong,
              ...INumericSx,
            }}
          >
            {hasAnyDelta ? `${sumDelta >= 0 ? '+' : ''}${Math.round(sumDelta)}€` : '—'}
          </Box>
        </Typography>
        {onSeeMore && (
          <Button
            size="small"
            endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
            onClick={onSeeMore}
            sx={{
              fontSize: `${IFontSizes.xs}px`,
              fontWeight: IFontWeights.label,
              color: IC.ai,
              textTransform: 'none',
              minWidth: 0,
              px: 0.75,
              py: 0.25,
              '&:hover': { background: IC.aiSoft },
            }}
          >
            {t('insights_v2_actions_see_more')}
          </Button>
        )}
      </Box>
      <style>{`@keyframes ins-v2-shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
    </Paper>
  );
};

export default ActionsInProgress;
